import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Images } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { SHOP_GALLERY } from "@/i18n/translations";
import api, { API } from "@/lib/api";

const VISIBLE_COUNT = 12;

export const Gallery = () => {
  const { t } = useLang();
  const g = t.gallery;
  const [active, setActive] = useState(null);
  const [photos, setPhotos] = useState(SHOP_GALLERY);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    api.get("/gallery")
      .then((res) => {
        const urls = res.data.map((img) => (img.url.startsWith("http") ? img.url : `${API}/${img.url}`));
        if (urls.length) setPhotos(urls);
      })
      .catch(() => { /* keep static fallback */ });
  }, []);

  const visible = showAll ? photos : photos.slice(0, VISIBLE_COUNT);

  return (
    <section id="gallery" className="relative py-24 sm:py-32 bg-stone-alt/50" data-testid="gallery">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl">
          <span className="text-sm font-bold uppercase tracking-[0.2em] text-bamboo">{g.tag}</span>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-brand-ink tracking-tight leading-[1.05]">{g.title}</h2>
          <p className="mt-4 text-brand-muted leading-relaxed text-lg">{g.subtitle}</p>
        </div>

        {/* Masonry — images keep full aspect ratio, whole dog always visible */}
        <div className="mt-12 columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:_balance]">
          {visible.map((src, i) => (
            <motion.button
              key={src}
              type="button"
              onClick={() => setActive(src)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
              className="group mb-4 block w-full overflow-hidden rounded-[1.25rem] shadow-[0_8px_24px_rgba(42,37,34,0.1)] no-a11y-underline break-inside-avoid"
              data-testid={`gallery-img-${i}`}
            >
              <img
                src={src}
                alt={`Care For Me — φωτογραφία ${i + 1}`}
                loading="lazy"
                className="w-full h-auto align-middle group-hover:scale-[1.04] transition-transform duration-500"
              />
            </motion.button>
          ))}
        </div>

        {photos.length > VISIBLE_COUNT && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3 text-brand-cream font-bold hover:bg-brand-hover transition-colors"
              data-testid="gallery-show-more"
            >
              <Images className="h-4 w-4" />
              {showAll ? g.show_less : `${g.show_more} (${photos.length - VISIBLE_COUNT}+)`}
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] grid place-items-center bg-brand-ink/85 backdrop-blur-sm p-5"
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setActive(null)}
              className="absolute top-5 right-5 grid place-items-center h-11 w-11 rounded-full bg-white/15 text-stone hover:bg-white/25 transition-colors"
              aria-label="Close"
              data-testid="gallery-lightbox-close"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              src={active}
              alt="Care For Me"
              className="max-h-[88vh] max-w-[92vw] w-auto h-auto rounded-2xl shadow-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
